import {
  AccessLevel,
  PermissionName,
  TableAction,
  TableSource,
  TableOption
} from '@tripointe/tripointe-cms/types';
import { Permission } from '@tripointe/tripointe-cms/data-access';

import { deepCopy } from './common.utils';
import { getAvailableEntityActionsForRole } from './roles-actions-matrix';

const allPermissions = [
  PermissionName.reports,
  PermissionName.communities,
  PermissionName.divisions,
  PermissionName.plans,
  PermissionName.homes,
  PermissionName.phases,
  PermissionName.documents,
  PermissionName.schedule,
  PermissionName.contacts,
  PermissionName.messages,
  PermissionName.users
];

const USER_ROLE_DEFAULT_PERMISSIONS = [PermissionName.homes, PermissionName.messages];

export const getAvailablePermissionsForRole = (role: AccessLevel): PermissionName[] => {
  switch (role) {
    case AccessLevel.platformAdministrator:
    case AccessLevel.builderAdministrator:
    case AccessLevel.divisionAdministrator:
      return allPermissions;
    case AccessLevel.administrator:
      return [
        PermissionName.reports,
        PermissionName.communities,
        PermissionName.plans,
        PermissionName.homes,
        PermissionName.messages,
        PermissionName.users
      ];
    case AccessLevel.user:
      return [PermissionName.reports, PermissionName.homes, PermissionName.messages];
    default:
      return [];
  }
};

export const roleHasPermission = (role: AccessLevel) => (permission: Partial<Permission>) => {
  const availablePermissionsForRole = getAvailablePermissionsForRole(role);

  return availablePermissionsForRole.includes(permission.name as PermissionName);
};

export const roleCanPerformAction = (
  role: AccessLevel,
  action: TableAction,
  entityName?: string
) => {
  const availableActionsForRole = getAvailableEntityActionsForRole(role, entityName);

  return availableActionsForRole.includes(action);
};

// filter data-source options by role
export const filterDataSourceOptionsByRole = (
  dataSourceWithConfig: any,
  role: AccessLevel,
  entityName?: string
) => {
  const dataSourceCopy = deepCopy(dataSourceWithConfig) as TableSource;

  const modifiedOptions = getAvailableOptionsForRole(dataSourceCopy.options, role, entityName);
  const modifiedOptionsPerRow = dataSourceCopy.optionsPerRow
    ? dataSourceCopy.optionsPerRow.map(options =>
        getAvailableOptionsForRole(options, role, entityName)
      )
    : null;

  const hasEmptyOptions = isEmptyOptions(modifiedOptions) && !modifiedOptionsPerRow;
  const hasEmptyOptionsPerRow =
    modifiedOptionsPerRow && modifiedOptionsPerRow.every(isEmptyOptions);

  if (hasEmptyOptions || hasEmptyOptionsPerRow) {
    const optionColumnIndex = dataSourceCopy.displayedColumns.length - 1;

    dataSourceCopy.displayedColumns = dataSourceCopy.displayedColumns.slice(0, optionColumnIndex);
  }

  return {
    ...dataSourceCopy,
    options: modifiedOptions,
    optionsPerRow: modifiedOptionsPerRow
  };
};

const isEmptyOptions = (options: any[]) => {
  return (
    options.length === 0 || (options.length === 1 && options[0].action === TableAction.mobileMenu)
  );
};

const getAvailableOptionsForRole = (
  options: TableOption[],
  role: AccessLevel,
  entityName?: string
) => {
  return options.filter(option =>
    roleCanPerformAction(role, option.action as TableAction, entityName)
  );
};

export const getSelectedPermissionsIdsForRoleUser = (
  permissions: Partial<Permission>[]
): number[] => {
  return permissions
    .filter(permission => USER_ROLE_DEFAULT_PERMISSIONS.includes(permission.name as PermissionName))
    .map(permission => permission.id);
};
